/**
 * Shared types for the zero-knowledge crypto layer.
 * These describe ONLY the encrypted wire/storage shapes — never plaintext.
 */

/** KDF parameters. Stored per-user on the server (they are not secret). */
export type KdfParams =
  | { algorithm: 'argon2id'; memoryKiB: number; iterations: number; parallelism: number; hashLengthBits: 256 }
  | { algorithm: 'pbkdf2'; iterations: number; hash: 'SHA-256'; hashLengthBits: 256 };

/** Recommended Argon2id parameters (OWASP-aligned). Tune per environment. */
export const DEFAULT_ARGON2_PARAMS: KdfParams = {
  algorithm: 'argon2id',
  memoryKiB: 64 * 1024, // 64 MiB
  iterations: 3,
  parallelism: 4,
  hashLengthBits: 256,
};

export const DEFAULT_PBKDF2_PARAMS: KdfParams = {
  algorithm: 'pbkdf2',
  iterations: 600_000,
  hash: 'SHA-256',
  hashLengthBits: 256,
};

/** Plaintext shape of a vault item's secret fields — lives only client-side in memory. */
export interface ItemFields {
  title: string;
  username?: string;
  password?: string;
  url?: string;
  notes?: string;
  totp?: string;
  tags?: string[];
  customFields?: { name: string; value: string; type?: 'text' | 'hidden' }[];
}

/** Encrypted item payload — what the server stores for item bodies. */
export interface EncryptedItem {
  /** AES-GCM(itemKey, ItemFields JSON), base64url. */
  ciphertext: string;
  /** 12-byte IV for the item body, base64url. */
  iv: string;
  /** AES-GCM(symKey, itemKey), packed iv||ciphertext base64url. */
  protectedItemKey: string;
}

/** Wrapped vault key + wrapped private key — stored on the user record. */
export interface ProtectedKeys {
  /** AES-GCM(encKey, symKey), packed base64url. */
  protectedVaultKey: string;
  /** RSA-OAEP public key, SPKI base64. */
  publicKey: string;
  /** RSA-OAEP private key (PKCS#8) encrypted with symKey, packed base64url. */
  protectedPrivateKey: string;
}

/** Sharing permission levels, enforced server-side on the share row. */
export type SharePermission = 'view' | 'edit' | 'reshare';
